import type { Worksheet } from "exceljs";
import type { DateTime } from "luxon";
import { PrintoutOrientation } from "../../../utils/models";
import type { PrintoutColumn } from "../types";

interface ApplyPageSettingsArgs {
	worksheet: Worksheet;
	enabledColumns: PrintoutColumn[];
	printNotes: boolean;
	title: string;
	date: DateTime;
	orientation: PrintoutOrientation | undefined;
	fitToPage?: boolean;
	fitToWidth?: number;
	fitToHeight?: number;
}

/**
 * Apply A4 print settings, headers, and footers to a worksheet (mapping §9.5).
 *
 * Orientation auto-detection (mapping §8.4): landscape if rendered column count > 4, else portrait.
 * The explicit `orientation` setting takes precedence over auto-detect when provided.
 *
 * Header row 1-4 is configured to repeat on each printed page so multi-page tables keep their
 * headers visible.
 */
export const applyPageSettings = ({
	worksheet,
	enabledColumns,
	printNotes,
	title,
	date,
	orientation,
	fitToPage,
	fitToWidth,
	fitToHeight,
}: ApplyPageSettingsArgs): void => {
	const resolvedOrientation: PrintoutOrientation =
		orientation ?? (enabledColumns.length + (printNotes ? 1 : 0) > 4 ? PrintoutOrientation.Landscape : PrintoutOrientation.Portrait);

	worksheet.pageSetup = {
		paperSize: 9, // A4
		orientation: resolvedOrientation,
		fitToPage: fitToPage ?? true,
		fitToWidth: fitToWidth ?? 1,
		fitToHeight: fitToHeight ?? 0,
		margins: { left: 0.4, right: 0.4, top: 0.6, bottom: 0.6, header: 0.3, footer: 0.3 },
		horizontalCentered: true,
		printTitlesRow: "1:4",
	};

	worksheet.headerFooter = {
		oddHeader: `&C&B${title}&R${date.toFormat("yyyy-MM-dd")}`,
		oddFooter: "&LPrinted: &D &T&CPage &P of &N",
	};
};
